import React, { useMemo } from "react";
import { Col, Row, Table, Button } from "react-bootstrap";
import { chunkArray } from "../../../../utils/array";
import { HugeiconsIcon } from "@hugeicons/react";
import { PencilEdit02Icon } from "@hugeicons/core-free-icons";

export default function UserDetailsTable({ userData, entity, docs, onEditMobile, onEditEmail, medicalHistory = [] }) {
  
  const renderMedicalValue = (value) => {
    if (value === null || value === undefined || value === "") return "NA";
    if (React.isValidElement(value)) return value;

    if (Array.isArray(value)) {
      if (value.length === 0) return "NA";
      const allPrimitive = value.every(
        (v) =>
          v === null ||
          v === undefined ||
          v === "" ||
          ["string", "number", "boolean"].includes(typeof v)
      );
      if (allPrimitive) {
        return value
          .filter((v) => v !== null && v !== undefined && v !== "")
          .map((v) => String(v))
          .join(", ") || "NA";
      }

      return (
        <div className="d-flex flex-column gap-1">
          {value.map((v, idx) => (
            <div key={idx}>{renderMedicalValue(v)}</div>
          ))}
        </div>
      );
    }

    const valueType = typeof value;
    if (["string", "number", "boolean"].includes(valueType)) return String(value);

    if (valueType === "object") {
      // Common injury_history item shape: { date, name, documents }
      const hasInjuryShape =
        Object.prototype.hasOwnProperty.call(value, "date") ||
        Object.prototype.hasOwnProperty.call(value, "name") ||
        Object.prototype.hasOwnProperty.call(value, "documents");

      if (hasInjuryShape) {
        const injuryName = value?.name ? String(value.name) : "Injury";
        const injuryDate = value?.date ? String(value.date) : "";
        const documentsRaw = value?.documents;
        const documents = Array.isArray(documentsRaw)
          ? documentsRaw
          : documentsRaw
            ? [documentsRaw]
            : [];
        const docUrls = documents
          .map((d) => (typeof d === "string" ? d : d?.doc_file || d?.url))
          .filter(Boolean);

        return (
          <div>
            <div>{[injuryName, injuryDate].filter(Boolean).join(" - ")}</div>
            {docUrls.length > 0 && (
              <div className="d-flex flex-wrap gap-2 mt-1">
                {docUrls.map((url, idx) => (
                  <a
                    key={idx}
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="doc-link"
                  >
                    Doc {idx + 1}
                  </a>
                ))}
              </div>
            )}
          </div>
        );
      }

      try {
        return JSON.stringify(value);
      } catch (e) {
        return "NA";
      }
    }

    return String(value);
  };

  const idTypeReadableMap = {
    aadhaar: "Aadhaar Card",
    "aadhaar card": "Aadhaar Card",
    pan_card: "pancard",
    pancard: "Pan Card",
    driving_license: "Driving License",
    birth_certificate: "Birth Certificate",
    tenth_certificate: "10th Certificate",
    cancelled_cheque: "Cancelled Cheque",
    passbook_photo: "Passbook Photo",
  };

  const getDocByName = (docName) =>
    docs?.find(
      (d) => (d?.doc_name || "").toLowerCase() === (docName || "").toLowerCase()
    );

  const fullName = useMemo(
    () => [userData?.first_name, userData?.middle_name, userData?.last_name].filter(Boolean).join(" "),
    [userData?.first_name, userData?.middle_name, userData?.last_name]
  );

  const identificationType = idTypeReadableMap[entity?.identity_type] || entity?.identity_type || "NA";
  const idDoc = getDocByName(entity?.identity_type || "aadhaar");


  const personalDetails = {
    "Full Name": fullName || "NA",
    Gender: entity?.gender === "M" ? "Male" : entity?.gender === "F" ? "Female" : entity?.gender || "NA",
    "Email ID": userData?.email || "NA",
    "Mobile Number": entity?.mobile || "NA",
    Identification: identificationType,
    "Identification Document": idDoc?.doc_file || "NA",
    Landmark: entity?.landmark || "NA",
    Country: "India",
    State: entity?.state_name || "NA",
    City: entity?.city_name || "NA",
    Pincode: entity?.pincode || "NA",
    DOB: entity?.dob || "NA",
    "Full Address": entity?.address || "NA",
  };

  console.log(entity);;
  const bankDetails = {
    "Bank Name": entity?.bank_name || "NA",
    "Name As Bank": entity?.account_holder_name || "NA",
    "IFSC Code": entity?.ifsc_code || "NA",
    "Branch Name": entity?.branch_name || "NA",
    "Account Number": entity?.bank_account_number || "NA",
  }

  const sportsDetails = {
    "Sport": entity?.ref_sport?.sport_name || "NA",
    "Kit Size": entity?.kit_size_name || "NA",
    "Shoes Size": entity?.shoe_size || "NA",
    "": ""

  }

  const additionalDocuments = userData?.indivisual_docs || [];
  const documents = additionalDocuments.filter((item) => {
    return item.doc_name !== "aadhaar" && item.doc_name !== "profile_image";
  })

  const detailEntries = Object.entries(personalDetails);
  const bankEntries = Object.entries(bankDetails);
  const sportEntries = Object.entries(sportsDetails);

  const chunkedRows = chunkArray(detailEntries, 3);
  const bankRows = chunkArray(bankEntries, 3)
  const sportRow = chunkArray(sportEntries, 3)
  const additionalDocsRows = chunkArray(documents, 3)



  return (
    <Row className="user-details-table-row">
      <Col md={12} className="mb-4">
        <h4>Personal Details</h4>
        <Table borderless size="sm" className="user-details-table">
          <tbody>
            {chunkedRows.map((row, rowIndex) => (
              <tr key={rowIndex} className="user-details-table-row-item">
                {row.map(([label, value], colIndex) => {
                  let displayValue = value;

                  if (label === "Identification") {
                    displayValue = identificationType;
                  }

                  if (label === "Identification Document") {
                    const isUrl = typeof value === "string" && value.startsWith("http");
                    displayValue = isUrl ? (
                      <a href={value} target="_blank" rel="noopener noreferrer" className="doc-link">
                        {identificationType}
                      </a>
                    ) : (
                      value || "NA"
                    );
                  }

                  const isEditableMobile = label === "Mobile Number";
                  const isEditableEmail = label === "Email ID";

                  return (
                    <td key={colIndex} style={{ width: "33.33%" }} className="user-detail-cell py-2">
                      <h5 className="detail-label mb-0">{label}</h5>
                      {isEditableMobile || isEditableEmail ? (
                        <div className="d-flex align-items-center justify-content-between gap-2">
                          <div className="detail-value">{displayValue || "NA"}</div>
                          <Button
                            variant="light"
                            size="sm"
                            onClick={isEditableMobile ? onEditMobile : onEditEmail}
                            className="edit-btn d-inline-flex align-items-center gap-1"
                          >
                            <span className="icon"><HugeiconsIcon icon={PencilEdit02Icon} size={16} /></span>
                            <span>Edit</span>
                          </Button>
                        </div>
                      ) : (
                        <div className="detail-value">{displayValue || "NA"}</div>
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </Table>
      </Col>

      <Col md={12} className="mb-4">
        <h4>Medical Details</h4>
        <Table borderless size="sm" className="user-details-table">
          <tbody>
            {[
              { label: "Height", value: medicalHistory?.height ? `${medicalHistory?.height} cm` : "NA" },
              { label: "Weight", value: medicalHistory?.weight ? `${medicalHistory?.weight} kg` : "NA" },
              { label: "Blood Group", value: medicalHistory?.blood_group || "NA" },
              {
                label: "Age",
                value: entity?.dob ? (() => {
                  const birthDate = new Date(entity.dob);
                  const today = new Date();
                  let age = today.getFullYear() - birthDate.getFullYear();
                  const m = today.getMonth() - birthDate.getMonth();
                  if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
                    age--;
                  }
                  return age;
                })() : "NA"
              },
              ...((Object.entries(medicalHistory || {})).filter(([key]) =>
                !["id", "user_id", "created_at", "updated_at", "height", "weight", "blood_group", "age", "injury_history"].includes(key)
              ).map(([key, value]) => ({
                label: key.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase()),
                value: value
              }))),
              ...(Array.isArray(medicalHistory?.injury_history) ? medicalHistory.injury_history.map((injury, i) => ({
                label: `Injury ${i + 1}`,
                value: injury
              })) : [])
            ]
              .reduce((resultArray, item, index) => {
                const chunkIndex = Math.floor(index / 3);
                if (!resultArray[chunkIndex]) {
                  resultArray[chunkIndex] = [];
                }
                resultArray[chunkIndex].push(item);
                return resultArray;
              }, [])
              .map((row, rowIndex) => (
                <tr key={rowIndex} className="user-details-table-row-item">
                  {row.map((item, colIndex) => (
                    <td key={colIndex} style={{ width: "33.33%" }} className="user-detail-cell py-2">
                      <h5 className="detail-label mb-0">{item.label}</h5>
                      <div className="detail-value">{renderMedicalValue(item.value)}</div>
                    </td>
                  ))}
                </tr>
              ))}
          </tbody>
        </Table>
      </Col>

      <Col md={12} className="mb-4">
        <h4>Bank Details</h4>
        <Table borderless size="sm" className="user-details-table mb-3" >
          <tbody>
            {bankRows.map((row, rowIndex) => (
              <tr key={rowIndex} className="user-details-table-row-item">
                {
                  row.map(([label, value], colIndex) => {
                    let displayValue = value;

                    return (
                      <td key={colIndex} style={{ width: "33.33%" }} className="user-detail-cell py-2">
                        <h5 className="detail-label mb-0">{label}</h5>
                        {true ? (
                          <div className="d-flex align-items-center justify-content-between gap-2">
                            <div className="detail-value">{displayValue || "NA"}</div>

                          </div>
                        ) : (
                          <div className="detail-value">{displayValue || "NA"}</div>
                        )}
                      </td>
                    );
                  }
                  )}
              </tr>
            ))}
          </tbody>
        </Table>
      </Col>

      <Col md={12} className="mb-3">
        <h4>Sports Details</h4>
        <Table borderless size="sm" className="user-details-table mb-0" >
          <tbody>
            {sportRow.map((row, rowIndex) => (
              <tr key={rowIndex} className="user-details-table-row-item">
                {
                  row.map(([label, value], colIndex) => {
                    let displayValue = value;

                    return (
                      <td key={colIndex} style={{ width: "33.33%" }} className="user-detail-cell py-2">
                        <h5 className="detail-label mb-0">{label}</h5>
                        {true ? (
                          <div className="d-flex align-items-center justify-content-between gap-2">
                            <div className="detail-value">{displayValue}</div>

                          </div>
                        ) : (
                          <div className="detail-value">{displayValue}</div>
                        )}
                      </td>
                    );
                  }
                  )}
              </tr>
            ))}
          </tbody>
        </Table>
      </Col>

      {
        additionalDocuments.length > 0 && (
          <Col md={12}>
            <h4>Additional Documents</h4>
            <Table borderless size="sm" className="user-details-table mb-0">
              <tbody>
                {additionalDocsRows.map((row, rowIndex) => (
                  <tr key={rowIndex} className="user-details-table-row-item">
                    {row.map((doc, colIndex) => (
                      <td key={colIndex} style={{ width: "33.33%" }} className="user-detail-cell py-2">
                        <h5 className="detail-label mb-0">
                          {{
                            "pan_card": "Pancard",
                            "dob_image": "Birth Certificate/ 10th Marksheet",
                            "bank_image": "Cancel cheque /passbook photo",
                            "aadhaar": "Aadhaar Card"
                          }[doc?.doc_name] || doc?.doc_name}
                        </h5>
                        <div className="detail-value">
                          <a href={doc.doc_file} target="_blank" rel="noopener noreferrer" className="doc-link">
                            {{
                              "pan_card": "Pancard",
                              "dob_image": "Birth Certificate/ 10th Marksheet",
                              "bank_image": "Cancel cheque /passbook photo",
                              "aadhaar": "Aadhaar Card"
                            }[doc?.doc_name] || doc?.doc_name}
                          </a>
                        </div>
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </Table>
          </Col>
        )
      }

    </Row>
  );
}
